import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';
import SelectField from './SelectField';
import { parseMailDate, skipWeekendToNextMonday } from '../utils/mailDates';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const pad = (n) => String(n).padStart(2, '0');

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isWeekendDay = (d) => {
  if (!d || Number.isNaN(d.getTime())) return false;
  const day = d.getDay();
  return day === 0 || day === 6;
};

const parseValue = (iso) => parseMailDate(iso);

const toIsoLocalAware = (d) => {
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatDisplay = (d) => {
  if (!d) return 'Termin wählen';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const nextMonday = (from = new Date()) => {
  const d = startOfDay(from);
  const day = d.getDay();
  const add = day === 1 ? 7 : (8 - day) % 7 || 7;
  d.setDate(d.getDate() + add);
  d.setHours(9, 0, 0, 0);
  return d;
};

const snapAwayFromWeekend = (date) => {
  if (!date || Number.isNaN(date.getTime())) return date;
  if (!isWeekendDay(date)) return date;
  const shifted = skipWeekendToNextMonday(date);
  if (!shifted || Number.isNaN(shifted.getTime())) return date;
  const next = new Date(shifted);
  next.setHours(date.getHours(), date.getMinutes(), 0, 0);
  return next;
};

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

/**
 * Kampagnen-DateTimePicker — Kalender + Uhrzeit, ISO-Value in/out
 * Standard: Sa/So nicht wählbar (Versand nur Mo–Fr).
 */
const DateTimePicker = ({
  value,
  onChange,
  disabled = false,
  minDate = null,
  disableWeekends = true,
  placeholder = 'Termin wählen',
  className = '',
  panelZIndex = 120,
}) => {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 320, placeAbove: false });

  const selected = useMemo(() => parseValue(value), [value]);
  const min = useMemo(() => (minDate ? startOfDay(minDate) : null), [minDate]);

  const initialCursor = selected || new Date();
  const [viewYear, setViewYear] = useState(initialCursor.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialCursor.getMonth());
  const [draftDay, setDraftDay] = useState(() => {
    const base = selected ? startOfDay(selected) : startOfDay(new Date(Date.now() + 86400000));
    return disableWeekends ? startOfDay(snapAwayFromWeekend(base) || base) : base;
  });
  const [hour, setHour] = useState(() => (selected ? selected.getHours() : 9));
  const [minute, setMinute] = useState(() => {
    if (!selected) return 0;
    const m = selected.getMinutes();
    return MINUTES.includes(m) ? m : Math.round(m / 5) * 5 % 60;
  });

  useEffect(() => {
    if (!open) return;
    let base = selected || new Date(Date.now() + 86400000);
    if (disableWeekends) base = snapAwayFromWeekend(base) || base;
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
    setDraftDay(startOfDay(base));
    setHour(base.getHours());
    const m = base.getMinutes();
    setMinute(MINUTES.includes(m) ? m : Math.round(m / 5) * 5 % 60);
  }, [open, selected, disableWeekends]);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelW = Math.min(340, Math.max(300, window.innerWidth - 24));
    const panelH = 420;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < panelH && rect.top > spaceBelow;
    let left = rect.left;
    if (left + panelW > window.innerWidth - 12) left = window.innerWidth - panelW - 12;
    if (left < 12) left = 12;
    setPos({
      top: placeAbove ? rect.top - 8 : rect.bottom + 8,
      left,
      width: panelW,
      placeAbove,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onWin = () => updatePosition();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e) => {
      const t = e.target;
      if (
        panelRef.current?.contains(t) ||
        triggerRef.current?.contains(t) ||
        // SelectField-Menü liegt als Portal außerhalb des Panels
        (t instanceof Element && t.closest('[data-select-field-menu]'))
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const cells = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const isDisabledDay = (day) => {
    if (!day) return true;
    if (min && startOfDay(day) < min) return true;
    if (disableWeekends && isWeekendDay(day)) return true;
    return false;
  };

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const commit = (day, h = hour, m = minute) => {
    if (!day || isDisabledDay(day)) return;
    let next = new Date(day);
    next.setHours(h, m, 0, 0);
    if (disableWeekends) {
      next = snapAwayFromWeekend(next) || next;
      if (isWeekendDay(next)) return;
    }
    if (next.getTime() < Date.now()) {
      next.setTime(Date.now() + 60_000);
    }
    onChange?.(toIsoLocalAware(next));
    setOpen(false);
  };

  const applyDraft = () => commit(draftDay, hour, minute);

  const applyPreset = (date) => {
    let next = date;
    if (disableWeekends) next = snapAwayFromWeekend(date) || date;
    if (min && startOfDay(next) < min) return;
    if (isDisabledDay(next)) return;
    setDraftDay(startOfDay(next));
    setHour(next.getHours());
    setMinute(next.getMinutes());
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
    commit(next, next.getHours(), next.getMinutes());
  };

  const presets = useMemo(() => {
    let tomorrow = startOfDay(new Date());
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    if (disableWeekends) tomorrow = snapAwayFromWeekend(tomorrow) || tomorrow;

    let in3 = startOfDay(new Date());
    in3.setDate(in3.getDate() + 3);
    in3.setHours(10, 0, 0, 0);
    if (disableWeekends) in3 = snapAwayFromWeekend(in3) || in3;

    const monday = nextMonday();

    return [
      { id: 'tomorrow', label: 'Morgen 09:00', date: tomorrow },
      { id: 'in3', label: 'In 3 Tagen', date: in3 },
      { id: 'monday', label: 'Nächster Mo', date: monday },
    ];
  }, [open, disableWeekends]);

  const today = startOfDay(new Date());

  return (
    <div className={`relative min-w-0 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`input-field !flex items-center gap-2 text-left disabled:opacity-50 disabled:cursor-not-allowed ${
          open ? '!ring-2 !ring-primary-500 !border-transparent' : ''
        }`}
      >
        <CalendarDays className="w-4 h-4 flex-shrink-0 text-neutral-400" />
        <span
          className={`min-w-0 flex-1 truncate ${
            selected ? '' : 'text-neutral-400 dark:text-neutral-500'
          }`}
        >
          {selected ? formatDisplay(selected) : placeholder}
        </span>
        {selected && !disabled ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange?.(null);
              }
            }}
            className="p-0.5 rounded text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 flex-shrink-0"
            title="Termin löschen"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0 rotate-90" />
        )}
      </button>

      {createPortal(
        <AnimatePresence>
          {open ? (
            <motion.div
              ref={panelRef}
              key="dtp-panel"
              initial={{ opacity: 0, y: pos.placeAbove ? 6 : -6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: pos.placeAbove ? 6 : -6, scale: 0.98 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: 'fixed',
                top: pos.placeAbove ? undefined : pos.top,
                bottom: pos.placeAbove ? window.innerHeight - pos.top : undefined,
                left: pos.left,
                width: pos.width,
                zIndex: panelZIndex,
              }}
              className="rounded-2xl border border-stone-200/90 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl shadow-stone-900/10 dark:shadow-black/40 overflow-hidden"
            >
              <div className="relative overflow-hidden">
                <div
                  className="pointer-events-none absolute inset-0 opacity-90"
                  style={{
                    background:
                      'radial-gradient(120% 80% at 0% 0%, rgb(var(--color-primary-500) / 0.12), transparent 55%), radial-gradient(90% 70% at 100% 0%, rgb(var(--color-primary-400) / 0.08), transparent 50%)',
                  }}
                />
                <div className="relative px-4 pt-4 pb-3 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => shiftMonth(-1)}
                    className="p-1.5 rounded-lg text-stone-500 hover:text-stone-900 dark:text-neutral-400 dark:hover:text-white hover:bg-white/70 dark:hover:bg-neutral-800 transition-colors"
                    aria-label="Vorheriger Monat"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="text-center min-w-0">
                    <p className="text-sm font-semibold text-stone-900 dark:text-white tracking-tight">
                      {MONTHS[viewMonth]} {viewYear}
                    </p>
                    {disableWeekends ? (
                      <p className="text-[10px] text-stone-400 dark:text-neutral-500 mt-0.5">
                        Sa/So nicht wählbar
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => shiftMonth(1)}
                    className="p-1.5 rounded-lg text-stone-500 hover:text-stone-900 dark:text-neutral-400 dark:hover:text-white hover:bg-white/70 dark:hover:bg-neutral-800 transition-colors"
                    aria-label="Nächster Monat"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="relative px-3 pb-2">
                  <div className="grid grid-cols-7 gap-0.5 mb-1">
                    {WEEKDAYS.map((w) => {
                      const weekendHeader = disableWeekends && (w === 'Sa' || w === 'So');
                      return (
                        <div
                          key={w}
                          className={`h-7 flex items-center justify-center text-[10px] font-semibold uppercase tracking-wide ${
                            weekendHeader
                              ? 'text-stone-300 dark:text-neutral-700'
                              : 'text-stone-400 dark:text-neutral-500'
                          }`}
                        >
                          {w}
                        </div>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5">
                    {cells.map((day, idx) => {
                      if (!day) {
                        return <div key={`e-${idx}`} className="h-9" />;
                      }
                      const disabledDay = isDisabledDay(day);
                      const isSelected = sameDay(day, draftDay) && !disabledDay;
                      const isToday = sameDay(day, today);
                      return (
                        <button
                          key={day.toISOString()}
                          type="button"
                          disabled={disabledDay}
                          title={
                            disableWeekends && isWeekendDay(day)
                              ? 'Wochenende — kein Versand'
                              : undefined
                          }
                          onClick={() => {
                            if (disabledDay) return;
                            setDraftDay(startOfDay(day));
                          }}
                          className={`relative h-9 rounded-xl text-[13px] font-medium transition-all duration-150 ${
                            disabledDay
                              ? 'text-stone-300 dark:text-neutral-700 cursor-not-allowed opacity-50'
                              : isSelected
                                ? 'bg-stone-900 text-white dark:bg-white dark:text-neutral-900 shadow-md scale-[1.04]'
                                : isToday
                                  ? 'text-primary-700 dark:text-primary-300 bg-primary-50/80 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50'
                                  : 'text-stone-700 dark:text-neutral-200 hover:bg-stone-100 dark:hover:bg-neutral-800'
                          }`}
                        >
                          {day.getDate()}
                          {isToday && !isSelected ? (
                            <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-stone-100 dark:border-neutral-800 bg-stone-50/80 dark:bg-neutral-950/50">
                <div className="flex items-center gap-2 mb-2.5">
                  <Clock className="w-3.5 h-3.5 text-stone-400" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500 dark:text-neutral-400">
                    Uhrzeit
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <SelectField
                    value={hour}
                    onChange={(e) => setHour(Number(e.target.value))}
                    wrapperClassName="flex-1"
                    className="w-full rounded-xl border border-stone-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm font-semibold text-stone-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>
                        {pad(h)} Uhr
                      </option>
                    ))}
                  </SelectField>
                  <span className="text-stone-400 font-semibold">:</span>
                  <SelectField
                    value={minute}
                    onChange={(e) => setMinute(Number(e.target.value))}
                    wrapperClassName="flex-1"
                    className="w-full rounded-xl border border-stone-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm font-semibold text-stone-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {pad(m)} Min
                      </option>
                    ))}
                  </SelectField>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-3">
                  {presets.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p.date)}
                      className="px-2.5 py-1 rounded-full text-[11px] font-medium border border-stone-200 dark:border-neutral-700 text-stone-600 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:border-primary-300 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                <div className="flex items-center justify-end gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="btn-secondary text-xs !py-1.5 !px-3"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={applyDraft}
                    disabled={isDisabledDay(draftDay)}
                    className="btn-primary text-xs !py-1.5 !px-3 disabled:opacity-50"
                  >
                    Übernehmen
                  </button>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default DateTimePicker;
